import {
  mapLeadDetail,
  type DiscoveryAnswerBody,
  type DiscoveryResponse,
  type EnrichmentState,
  type LeadDetail,
  type LeadDetailApi,
} from '@viq-admin/screens';
import { superadminFetch, superadminFetchRaw } from '../../api/dataProvider';
import { createRawnote, editLead, editRawnote } from '../../api/leadEdit';
import {
  updateConferenceResponse,
  deleteConferenceResponse,
} from '../../api/conferenceResponses';

/** Reads a filename from `Content-Disposition`, falling back to a generic name. */
function readFilename(response: Response, fallback: string): string {
  const cd = response.headers.get('Content-Disposition') ?? '';
  const match = cd.match(/filename="([^"]+)"/);
  return match?.[1] ?? fallback;
}

/**
 * superadminFetch's HttpError carries `statusCode`; LeadEnrichmentApi (and
 * ContactEnrichmentPanel, which distinguishes 409-suppressed from
 * 422-thin-identity) expects `status`. Rethrow under the field name the
 * panel actually reads, message preserved, rather than have the 409/422
 * branches silently never fire against the real backend.
 */
async function withEnrichmentErrorShape<T>(request: Promise<T>): Promise<T> {
  try {
    return await request;
  } catch (err: unknown) {
    const e = err as { statusCode?: number; message?: string };
    throw { status: e.statusCode, message: e.message };
  }
}

/**
 * Super-admin plane wiring for the shared Lead Detail body.
 *
 * The console's endpoints return the SAME raw shapes the customer portal's
 * mapper/panels expect, so the shared `mapLeadDetail` + panels render an exact
 * mirror. Archive/restore/update hit the super-admin lead endpoints, keyed by
 * lead id only (no `?companyId=`); discovery hits the per-person discovery
 * endpoint.
 */
export const leadDetailApi: LeadDetailApi = {
  fetchLeadDetail: async (id: string): Promise<LeadDetail> =>
    mapLeadDetail(await superadminFetch<unknown>(`/v1/superadmin/leads/${id}`)),

  archiveLead: async (id: string): Promise<void> => {
    await superadminFetch<void>(`/v1/superadmin/leads/${id}/archive`, {
      method: 'POST',
    });
  },

  restoreLead: async (id: string): Promise<void> => {
    await superadminFetch<void>(`/v1/superadmin/leads/${id}/restore`, {
      method: 'POST',
    });
  },

  updateLead: (id, fields, expectedVersion) => editLead(id, fields, expectedVersion),

  updateNote: (leadId, noteId, fields, expectedVersion) =>
    editRawnote(leadId, noteId, fields, expectedVersion),

  createNote: (leadId, text) => createRawnote(leadId, text),

  fetchDiscovery: (personId: string): Promise<DiscoveryResponse | null> =>
    superadminFetch<DiscoveryResponse | null>(
      `/v1/superadmin/persons/${personId}/discovery`,
    ),

  // Card-image access, always on for super admins. The portal's twin lives in
  // apps/portal/src/api/cardImages.ts and is offered only to a company_admin
  // whose company has card images on.
  photos: {
    fetchImage: async (photoId: string): Promise<Blob> => {
      const res = await superadminFetchRaw(
        `/v1/superadmin/rawphotos/${photoId}/card-image`,
      );
      return res.blob();
    },

    downloadImage: async (photoId: string): Promise<void> => {
      const res = await superadminFetchRaw(
        `/v1/superadmin/rawphotos/${photoId}/card-image?download=1`,
      );
      const blob = await res.blob();
      const filename = readFilename(res, `card-image-${photoId}.jpg`);
      const url = URL.createObjectURL(blob);
      try {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        try {
          a.click();
        } finally {
          try {
            document.body.removeChild(a);
          } catch {
            // Node may already be detached (e.g. click() navigated/removed
            // it) — swallow so we don't mask the original error, if any.
          }
        }
      } finally {
        URL.revokeObjectURL(url);
      }
    },
  },

  // Contact-enrichment access. Present ONLY on the console's api object — the
  // customer portal's leadDetailApi deliberately omits it, which is what
  // hides the ContactEnrichmentPanel there. Do not copy this into apps/portal.
  enrichment: {
    fetchEnrichment: (personId: string): Promise<EnrichmentState> =>
      withEnrichmentErrorShape(
        superadminFetch<EnrichmentState>(`/v1/superadmin/leads/${personId}/enrichment`),
      ),

    enrich: (personId: string, opts?: { providers?: string[] }): Promise<EnrichmentState> =>
      withEnrichmentErrorShape(
        superadminFetch<EnrichmentState>(`/v1/superadmin/leads/${personId}/enrich`, {
          method: 'POST',
          body: JSON.stringify(opts ?? {}),
        }),
      ),

    rejectCandidate: (personId: string, candidateId: string): Promise<void> =>
      withEnrichmentErrorShape(
        superadminFetch<{ rejected: true }>(
          `/v1/superadmin/leads/${personId}/enrichment/${candidateId}/reject`,
          { method: 'POST' },
        ),
      ).then(() => undefined),
  },
};

/**
 * The console's Discovery writes need the target tenant. Take it from the LEAD
 * (`person.companyId`, mapped in Task 9) rather than the global company-scope
 * picker: a super_admin can open a lead outside the current scope, and the
 * write must still land on that lead's tenant.
 */
export function discoveryClosures(companyId: string) {
  return {
    updateDiscoveryAnswer: (
      conferenceId: string, personId: string, questionId: string,
      body: DiscoveryAnswerBody,
    ) => updateConferenceResponse(conferenceId, personId, questionId, body, companyId),
    deleteDiscoveryAnswer: (
      conferenceId: string, personId: string, questionId: string,
    ) => deleteConferenceResponse(conferenceId, personId, questionId, companyId),
  };
}
